import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { makeStyles } from 'tss-react/mui';
import Grid from '@mui/material/Grid';
import ArrowLeft from '@mui/icons-material/ArrowLeft';
import ArrowRight from '@mui/icons-material/ArrowRight';
import ImageFrameDots from './ImageFrameDots';
import ToolTippedIconButton from './UI/ToolTippedIconButton';

const useStyles = makeStyles()((theme) => ({
  row: {
    borderRadius: theme.shape.borderRadius,
    padding: '6px 12px',
    borderTop: '1px solid #edf0e8',
    boxSizing: 'border-box',
  },
  highlighted: {
    backgroundColor: theme.palette.action.selected,
    boxShadow: `inset 3px 0 ${theme.palette.primary.main}`,
  },
  lineWrapper: {
    position: 'relative',
    width: '100%',
  },
  verticalLine: {
    position: 'absolute',
    height: '28px',
    width: '2px',
    background: theme.palette.primary.main,
    top: 'calc(50% - 14px)',
    // left will be set dynamically
  },
  horizontalLine: {
    position: 'absolute',
    height: '2px',
    background: '#dce4d5',
    width: '100%',
    // 1px to account for top at 50% rather than middle
    top: 'calc(50% - 1px)',
  },
  sliderRoot: {
    display: 'block',
    top: '50%',
    transform: 'translateY(-50%)',
  },
}));

const ImageFrameSlider = ({
  numFrames,
  startIndex,
  imageName,
  isHighlighted = false,
  imageFrameCount,
  onClickIncrease = () => {},
  onClickDecrease = () => {},
  onFrameClick = () => {},
  absoluteCurrentIndex,
  emptyFrameIndices = [],
  entries,
  timePosition,
  timingLabel,
  finishedPosition = 100,
}) => {
  const { classes, cx } = useStyles();
  const linePosition = `calc(${timePosition ?? (absoluteCurrentIndex / Math.max(1, numFrames - 1)) * 100}% - 1px)`;

  const handleFrameClick = useCallback(
    (frameIndex) => {
      onFrameClick(imageName, frameIndex);
    },
    [onFrameClick, imageName]
  );

  return (
    <Grid sx={{ width: '100%' }} container role="group" aria-label={`${imageName} timeline`}
      className={cx(classes.row, isHighlighted && classes.highlighted)}>
      <Grid size={{ xs: 12, md: 4 }} style={{ minWidth: 0 }}>
        <ListItem sx={{ py: 0.5, px: 0.5 }}>
          <ListItemText primary={imageName} secondary={timingLabel}
            primaryTypographyProps={{ noWrap: true, title: imageName, fontSize: 13, fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: 12, mt: 0.25 }} />
        </ListItem>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }} style={{ minWidth: 0 }}>
        <Box display="flex" height="100%" minHeight={44}>
          <ToolTippedIconButton
            disabled={imageFrameCount < 2}
            tooltipProps={{ title: 'Move Animation Start Left', placement: 'top' }}
            iconButtonProps={{
              variant: 'text',
              size: 'small',
              'aria-label': 'move animation start left',
              onClick: onClickDecrease,
            }}
          >
            <ArrowLeft />
          </ToolTippedIconButton>
          <div className={classes.lineWrapper}>
            {finishedPosition < 100 && <Box aria-hidden="true" sx={{
              position: 'absolute', top: 0, bottom: 0, right: 0,
              left: `${finishedPosition}%`, bgcolor: 'action.hover',
              borderLeft: '1px dashed', borderColor: 'text.secondary',
            }} />}
            <div className={classes.verticalLine} style={{ left: linePosition }} />
            <div className={classes.horizontalLine} />
            <ImageFrameDots
              entries={entries}
              numFrames={numFrames}
              startIndex={startIndex}
              imageFrameCount={imageFrameCount}
              emptyFrameIndices={emptyFrameIndices}
              onFrameClick={handleFrameClick}
            />
          </div>
          <ToolTippedIconButton
            disabled={imageFrameCount < 2}
            tooltipProps={{ title: 'Move Animation Start Right', placement: 'top' }}
            iconButtonProps={{
              variant: 'text',
              size: 'small',
              'aria-label': 'move animation start right',
              onClick: onClickIncrease,
            }}
          >
            <ArrowRight />
          </ToolTippedIconButton>
        </Box>
      </Grid>
    </Grid>
  );
};

ImageFrameSlider.propTypes = {
  finishedPosition: PropTypes.number,
  entries: PropTypes.array,
  timePosition: PropTypes.number,
  timingLabel: PropTypes.string,
  startIndex: PropTypes.number.isRequired,
  imageFrameCount: PropTypes.number.isRequired,
  imageName: PropTypes.string.isRequired,
  isHighlighted: PropTypes.bool,
  numFrames: PropTypes.number.isRequired,
  absoluteCurrentIndex: PropTypes.number.isRequired,
  onClickIncrease: PropTypes.func,
  onClickDecrease: PropTypes.func,
  onFrameClick: PropTypes.func,
  emptyFrameIndices: PropTypes.arrayOf(PropTypes.number),
};

export default ImageFrameSlider;
